/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLanguageOptions.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Custom hook to fetch language options from API.

 🔧 Notes:
    - Simple hook that fetches language options directly from API
    - No caching complexity - lightweight query
    - Provides loading state and error handling

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { buildApiUrl, getApiHeaders } from '../config/api';

interface VoiceOption {
  value: string;
  label: string;
  fullPath: string;
}

interface LanguageOption {
  value: string;
  label: string;
  voices: VoiceOption[];
}

export const useLanguageOptions = () => {
  const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadLanguageOptions = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      
      try {
        const response = await fetch(
          buildApiUrl('/api/telephony/voice-guides/language-options'),
          {
            method: 'GET',
            headers: getApiHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load language options');
        }

        const result = await response.json();
        if (result.success && result.data) {
          setLanguageOptions(result.data);
        }
      } catch (error) {
        console.error('Error loading language options:', error);
        setErrorMessage('Failed to load language options');
        
        // Fallback to basic options
        const fallbackOptions = [
          {
            value: 'en-us',
            label: 'English (US)',
            voices: [
              { value: 'emma', label: 'Emma (Female)', fullPath: '/usr/share/freeswitch/sounds/en/us/emma/' },
              { value: 'callie', label: 'Callie (Female)', fullPath: '/usr/share/freeswitch/sounds/en/us/callie/' }
            ]
          },
          {
            value: 'es-us',
            label: 'Español (US)',
            voices: [
              { value: 'paloma', label: 'Paloma (Female)', fullPath: '/usr/share/freeswitch/sounds/es/us/paloma/' }
            ]
          }
        ];
        
        setLanguageOptions(fallbackOptions);
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguageOptions();
  }, []);

  return {
    languageOptions,
    isLoading,
    errorMessage
  };
};

